import { CCardFooter } from "@coreui/react";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Divider,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import apis from "../../common/apis";
import { initialAlert, useAlert } from "../../provider/AlertContextProvider";
import { T_Hand } from "../../common/model/handModel";
import { T_File } from "../../common/model/fileModel";
import { AttachFile, Delete, ArrowUpward, ArrowDownward } from '@mui/icons-material';
import values from "../../common/values";

/**
 * FileItem 타입: 서버에서 내려온 기존 파일과 새로 선택한 파일을 통합 관리
 */
type FileItem = {
  id?: number;         // 기존 파일 고유 ID
  file?: File;         // 새로 업로드된 파일
  preview: string;     // 미리보기 URL
  isNew: boolean;      // 신규 업로드 여부
  sort:number
};

export default function HandEdit() {
  const { state } = useLocation();
  const iData = state?.item as T_Hand;
  const nav = useNavigate();
  const { alert, setAlert } = useAlert();

  const [data, setData] = useState<T_Hand>(iData);
  const [selectedFiles, setSelectedFiles] = useState<FileItem[]>([]);

  // 마운트 시: 기존 파일 매핑
  useEffect(() => {
  console.log("oData:",iData)
    if (iData?.fileList?.length) {
      const existing = iData.fileList.map((f: T_File,idx) => ({
        id: f.idx,
        preview: `${values.server.url}/upload/${f.fileUrl}`,
        isNew: false,
        sort:idx,
      }));
      setSelectedFiles(existing);
    }
  }, [iData]);

const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
  const files = e.target.files;
  if (!files) return;

  setSelectedFiles(prev => {
    // 기존 파일 개수만큼 오프셋을 잡아서
    const startSort = prev.length;

    // 각 파일에 대해 sort = startSort + index 로 설정
    const newItems: FileItem[] = Array.from(files).map((file, idx) => ({
      file,
      preview: URL.createObjectURL(file),
      isNew: true,
      sort: startSort + idx,
    }));

    return [...prev, ...newItems];
  });

  // input 초기화
  e.target.value = "";
};
  // 파일 삭제
  const handleRemove = (idx: number) => {
    const item = selectedFiles[idx];
    if (item.isNew) URL.revokeObjectURL(item.preview);
    setSelectedFiles((prev) => prev.filter((_, i) => i !== idx));
  };

  // 파일 순서 변경
// 수정 후 handleMove (sort 값까지 갱신)
const handleMove = (from: number, to: number) => {
  setSelectedFiles((prev) => {
    if (to < 0 || to >= prev.length) return prev;

    // 1) 요소 재배치
    const arr = [...prev];
    const [moved] = arr.splice(from, 1);
    arr.splice(to, 0, moved);

    // 2) 새로운 인덱스에 맞춰 sort 업데이트
    return arr.map((item, idx) => ({
      ...item,
      sort: idx,
    }));
  });
};
  // 저장 처리
  const saveData = () => {
    if (!data.title?.trim()) {
      setAlert({ ...alert, visible: true, title: "확인", msg: "제목을 입력해주세요" });
      return;
    }
    setAlert({
      ...alert,
      visible: true,
      title: "확인",
      msg: "정말 수정하시겠습니까?",
      buttonList: [
        {
          text: "예",
          variant: "contained",
          onPress: async () => {
            // 신규/기존 파일 구분 및 정렬 정보 포함
            const fileItems = selectedFiles.map((item, idx) => ({
              id: item.isNew ? undefined : item.id,
              file: item.isNew ? item.file : undefined,
              sort: idx,
            }));
            const payload: any = {
              fileMstIdx:data.fileMstIdx,
              idx: data.idx,
              title: data.title,
//              filelist: fileItems,
              upfiles:selectedFiles,
            };
            try {
              console.log("payload:",payload);
              const result = await apis.hand.handEdit(payload);
              if (result.code === '1') {
                setAlert({ ...alert, visible: true, title: "완료", msg: "수정이 완료되었습니다." });
                nav('/handy');
              } else {
                throw new Error('서버 오류');
              }
            } catch (e) {
              setAlert({ ...alert, visible: true, title: "오류", msg: "수정 중 오류가 발생했습니다." });
            }
          },
        },
        {
          text: "아니요",
          variant: "outlined",
          onPress: () => setAlert({ ...initialAlert }),
        },
      ],
    });
  };

  return (
    <Card>
      <CardHeader title="핸드로드 사용법 수정" />
      <CardContent>
        <Divider />
        <Table stickyHeader>
          <TableHead />
          <TableBody>
            <TableRow>
              <TableCell sx={{ width: 150 }}>제목</TableCell>
              <TableCell>
                <TextField
                  size="small"
                  fullWidth
                  value={data.title || ''}
                  onChange={(e) => setData({ ...data, title: e.target.value })}
                />
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={{ width: 150 }}>파일첨부</TableCell>
              <TableCell>
                <Button variant="contained" component="label" startIcon={<AttachFile />}>
                  파일 선택
                  <input
                    type="file"
                    hidden
                    multiple
                    accept="image/*"
                    onChange={handleFileChange}
                  />
                </Button>
                {selectedFiles.length > 0 && (
                  <Box mt={1}>
                    <Stack direction="row"   sx={{
                    rowGap: 2,     // theme.spacing(2) 만큼 세로 간격
                    columnGap: 1,  // theme.spacing(1) 만큼 가로 간격
                  }} flexWrap="wrap">
                      {selectedFiles.map((item, idx) => (
                        <Box
                          key={idx}
                          sx={{
                            position: 'relative',
                            width: 180,
                            height: 180,
                            border: '1px solid #ddd',
                            borderRadius: 1,
                            overflow: 'hidden',
                            padding:1
                          }}
                        >
                          <img
                            src={item.preview}
                            alt={item.file?.name || `file-${idx}`}
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          />
                          <Box
                            sx={{
                              position: 'absolute',
                              top: 2,
                              right: 2,
                              display: 'flex',
                              flexDirection: 'column',
                              gap: 0.5,
                            }}
                          >
                            <IconButton size="small" onClick={() => handleRemove(idx)}>
                              <Delete fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              disabled={idx === 0}
                              onClick={() => handleMove(idx, idx - 1)}
                            >
                              <ArrowUpward fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              disabled={idx === selectedFiles.length - 1}
                              onClick={() => handleMove(idx, idx + 1)}
                            >
                              <ArrowDownward fontSize="small" />
                            </IconButton>
                          </Box>
                        </Box>
                      ))}
                    </Stack>
                  </Box>
                )}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </CardContent>
      <CCardFooter  style={{   display: 'flex',justifyContent:'center',textAlign:'center',paddingBottom:10, gap: '1rem'}} >
        <Button variant="contained" onClick={saveData}>
          저장
        </Button>
        <Button variant="outlined" onClick={() => nav(-1)}>
          취소
        </Button>
      </CCardFooter>
    </Card>
  );
}
